import React from "react";

import { useParams } from "react-router-dom";

import Panel from "ui/Panel";
import { useDateRange } from "providers/WithDateRange";
import { useQueryStringSearch } from "ui/QueryStringSearch";
import QueryOverviewGraph from "features/Queries/QueryOverviewGraph";

import { useUserPreferences } from "utils/hooks";
import { UserPreferences } from "utils/preferences";

import QueryTable from "./QueryTable";
import styles from "./style.module.scss";
import { GridSettingsButton } from "ui/Grid";

function QueryList() {
  const { databaseId } = useParams();
  const [qlPrefs, setQlPrefs] = useUserPreferences("queryList");
  const mergeQlPrefs = (newValues: Partial<UserPreferences["queryList"]>) => {
    setQlPrefs({ ...qlPrefs, ...newValues });
  };
  const setShowRead = (showRead: boolean) => {
    mergeQlPrefs({ showRead });
  };
  const setShowWrite = (showWrite: boolean) => {
    mergeQlPrefs({ showWrite });
  };
  const setShowOther = (showOther: boolean) => {
    mergeQlPrefs({ showOther });
  };
  const setDoCompare = (doCompare: boolean) => {
    mergeQlPrefs({ doCompare });
  };
  const qlDefaults: UserPreferences["queryList"] = {
    showRead: true,
    showWrite: true,
    showOther: true,
    doCompare: false,
  };
  const merged = { ...qlDefaults, ...qlPrefs };
  if (!merged.showRead && !merged.showWrite && !merged.showOther) {
    // We used to not protect against all statement types being turned off; work
    // around that here in case any users got themselves in this situation
    merged.showRead = true;
  }
  const { showRead, showWrite, showOther, doCompare } = merged;

  const [searchTerm, SearchInput] = useQueryStringSearch("q");

  const { from, to } = useDateRange();

  const statementTypes: string[] = [];
  if (showRead) {
    statementTypes.push("read");
  }
  if (showWrite) {
    statementTypes.push("write");
  }
  if (showOther) {
    statementTypes.push("other");
  }

  const queryTableHeader = (
    <div className="flex gap-[10px] border-t border-[#e8e8ee] pt-[5px] px-[10px] pb-[10px]">
      <form
        className={styles.queryFilter}
        onSubmit={(evt) => evt.preventDefault()}
      >
        <div className="flex">
          <input
            type="checkbox"
            checked={showRead}
            disabled={showRead && statementTypes.length === 1}
            name="filter[statement_type][read]"
            id="filter_statement_type_read"
            onChange={(evt) => setShowRead(evt.target.checked)}
          />
          <label htmlFor="filter_statement_type_read"> SELECT</label>

          <input
            type="checkbox"
            checked={showWrite}
            disabled={showWrite && statementTypes.length === 1}
            id="filter_statement_type_write"
            onChange={(evt) => setShowWrite(evt.target.checked)}
          />
          <label htmlFor="filter_statement_type_write">
            {" "}
            INSERT, UPDATE, DELETE
          </label>

          <input
            type="checkbox"
            checked={showOther}
            disabled={showOther && statementTypes.length === 1}
            id="filter_statement_type_other"
            onChange={(evt) => setShowOther(evt.target.checked)}
          />
          <label htmlFor="filter_statement_type_other"> DDL &amp; other</label>
        </div>
        <div className="grow shrink-0 basis-auto" />
        <div className="flex">
          <input
            type="checkbox"
            checked={doCompare}
            id="include_comparison"
            onChange={(evt) => setDoCompare(evt.target.checked)}
          />
          <label htmlFor="include_comparison"> Compare to 7 days ago</label>
        </div>
        <SearchInput name="filter[search]" debounceMs={300} />
      </form>
      <GridSettingsButton className="flex-shrink self-center" />
    </div>
  );

  return (
    <Panel title="Database Queries">
      <QueryOverviewGraph databaseId={databaseId} />
      {from && to && (
        <QueryTable
          header={queryTableHeader}
          searchTerm={searchTerm}
          databaseId={databaseId}
          compare={doCompare}
          startTs={from.unix()}
          endTs={to.unix()}
          statementTypes={statementTypes}
        />
      )}
    </Panel>
  );
}

export default QueryList;
